import React, { useState } from "react";

import { ListIcon as List, TreeIcon as Tree } from "@packages/ds";
import { FormattedMessage } from "react-intl";

import ListGroup from "Components/ListGroup";
import Toggle from "ds/Toggle";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { isDg3 } from "Libs/utils";
import { selectHasDedicatedDeployment } from "Reducers/environment/environment.selectors";
import { selectFeatureServiceTreeEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { selectSubscriptionDetails } from "Reducers/organization/subscription";
import { gitProjectSelector } from "Reducers/project/project";
import ServicesDisplay from "src/organization/common/containers/ServicesDisplay";
import { useAppSelector } from "Store/hooks";

import NoServices from "../NoServices";

import * as S from "./ServicesRoutesLayout.styles";

export interface ServicesRoutesLayoutProps {
  children: React.ReactNode;
}
interface CurrentServiceDecodedParams {
  environmentId: string;
  organizationId: string;
  projectId: string;
}

const ServicesRoutesLayout = ({ children }: ServicesRoutesLayoutProps) => {
  const [listService, setListService] = useState(true);

  const { environmentId, organizationId, projectId } =
    useDecodedParams<CurrentServiceDecodedParams>();

  const project = useSelectorWithUrlParams(gitProjectSelector);

  const dedicatedDeployment = useSelectorWithUrlParams(
    selectHasDedicatedDeployment
  );

  const subscription = useAppSelector(state =>
    selectSubscriptionDetails(state, {
      organizationId,
      subscriptionId: project?.getSubscriptionId()
    })
  );

  const serviceTreeEnabled = useAppSelector(selectFeatureServiceTreeEnabled);

  const isDg3Project = isDg3(subscription?.enterprise_tag, dedicatedDeployment);

  const serviceTreeOrDg3 = serviceTreeEnabled || isDg3Project;

  return (
    <S.EnvironmentServicesLayout>
      <NoServices
        organizationId={organizationId}
        environmentId={environmentId}
        projectId={projectId}
      >
        <ListGroup
          className="service-tree-wrapper col-3"
          aria-labelledby="services-heading"
        >
          <S.Title tag="h2" id="services-heading">
            <S.ToolBar>
              <FormattedMessage id="apps_services" />

              {serviceTreeOrDg3 && (
                <S.ToggleWrapper>
                  <Toggle
                    onPressedChange={() => {
                      setListService(false);
                    }}
                    layout="tree"
                    toggleType="icon"
                    pressed={!listService}
                    text={<Tree color="var(--mode-vector-neutral-default)" />}
                  />
                  <Toggle
                    layout="list"
                    onPressedChange={() => {
                      setListService(true);
                    }}
                    toggleType="icon"
                    pressed={listService}
                    text={<List color="var(--mode-vector-neutral-default)" />}
                  />
                </S.ToggleWrapper>
              )}
            </S.ToolBar>
          </S.Title>
          {serviceTreeOrDg3 && (
            <ServicesDisplay width="316px" listMode={listService} />
          )}
        </ListGroup>
        {children}
      </NoServices>
    </S.EnvironmentServicesLayout>
  );
};

export default ServicesRoutesLayout;
